/**
 * Mock Dashboard Data
 *
 * Simulates data that will eventually come from graph queries on nodes/edges.
 * Structure matches the canonical schema:
 * - Nodes: Deal, FundraisingOpportunity, Investor, Investment, Organization, Person, etc.
 * - Edges: DEAL_WITH, SOURCED_BY, COMMITTED_TO, APPLIES_TO, etc.
 * - AMFI: Metrics, Facts, Insights attached via HAS_METRIC, HAS_FACT, HAS_INSIGHT edges
 */

import type { WidgetConfig } from './WidgetRenderer';

// =============================================================================
// DEAL FLOW MODULE - Deal Pipeline Dashboard
// =============================================================================

export const dealFlowMockData: Record<string, WidgetConfig> = {
  // PRIMARY KPIs - Industry Standard Deal Flow Metrics
  'Active Deals': {
    value: 24,
    label: 'Active Deals',
    secondaryLabel: 'In pipeline',
    trend: 12,
    color: 'primary',
  },
  'Pipeline Value': {
    value: 142000000,
    label: 'Pipeline Value',
    secondaryLabel: 'Total check sizes',
    format: 'currency',
    trend: 23,
  },
  'Win Rate': {
    value: 8.2,
    label: 'Win Rate',
    secondaryLabel: 'YTD (4 of 49 deals)',
    format: 'percentage',
    color: 'success',
  },
  'Avg Time to Close': {
    value: 94,
    label: 'Avg Days to Close',
    secondaryLabel: 'Sourced → Closed',
    color: 'info',
  },

  // SECONDARY KPIs
  'Deals This Month': {
    value: 12,
    label: 'New Deals',
    secondaryLabel: 'This month',
    trend: 20,
  },
  'Conversion Rate': {
    value: 62,
    label: 'Stage Conversion',
    secondaryLabel: 'First Look → Partner Mtg',
    format: 'percentage',
    color: 'secondary',
  },
  'In Due Diligence': {
    value: 6,
    label: 'In Due Diligence',
    secondaryLabel: '$48M total',
    color: 'info',
  },
  'Stale Deals': {
    value: 3,
    label: 'Stale Deals',
    secondaryLabel: 'No activity 30+ days',
    color: 'warning',
  },

  // Pipeline Funnel
  'Pipeline Funnel': {
    stages: [
      { label: 'Sourced', value: 45, color: '#94a3b8' },
      { label: 'First Look', value: 28, color: '#64748b' },
      { label: 'Partner Meeting', value: 15, color: '#3b82f6' },
      { label: 'Due Diligence', value: 6, color: '#8b5cf6' },
      { label: 'IC Review', value: 3, color: '#f59e0b' },
      { label: 'Term Sheet', value: 2, color: '#10b981' },
    ],
  },

  // Deals by Source - pie chart
  'Deals by Source': {
    data: [
      { label: 'Partner Network', value: 35 },
      { label: 'Inbound', value: 22 },
      { label: 'Conference', value: 18 },
      { label: 'Portfolio Referral', value: 12 },
      { label: 'Cold Outreach', value: 8 },
    ],
  },

  // Recent Activity
  'Recent Activity': {
    activities: [
      {
        id: '1',
        type: 'meeting',
        description: 'Partner meeting with Acme Corp founders',
        timestamp: '2 hours ago',
        entity: 'Acme Corp - Series A',
      },
      {
        id: '2',
        type: 'document',
        description: 'Received pitch deck from DataFlow AI',
        timestamp: '4 hours ago',
        entity: 'DataFlow AI - Seed',
      },
      {
        id: '3',
        type: 'deal',
        description: 'Moved CloudSync to Due Diligence',
        timestamp: 'Yesterday',
        entity: 'CloudSync - Series B',
      },
      {
        id: '4',
        type: 'meeting',
        description: 'IC presentation for TechVenture',
        timestamp: 'Yesterday',
        entity: 'TechVenture - Series A',
      },
      {
        id: '5',
        type: 'message',
        description: 'Follow-up call with FinanceAI CEO',
        timestamp: '2 days ago',
        entity: 'FinanceAI - Series A',
      },
    ],
  },

  // IC Calendar
  'IC Calendar': {
    items: [
      { id: '1', title: 'IC Review: CloudSync Series B', date: 'Tomorrow, 2:00 PM' },
      { id: '2', title: 'IC Review: DataFlow AI Seed', date: 'Dec 6, 10:00 AM' },
      { id: '3', title: 'IC Review: HealthTech Pro Series A', date: 'Dec 10, 2:00 PM' },
    ],
  },

  // Active Deals Table - proper table with full columns
  'Active Deals Table': {
    tableData: {
      columns: [
        { key: 'company', label: 'Company', align: 'left' },
        { key: 'stage', label: 'Stage', align: 'center' },
        { key: 'sector', label: 'Sector', align: 'left' },
        { key: 'leadPartner', label: 'Lead', align: 'left' },
        { key: 'checkSize', label: 'Check Size', format: 'currency', align: 'right' },
        { key: 'daysInStage', label: 'Days', align: 'right' },
      ],
      rows: [
        {
          id: '1',
          company: 'HealthTech Pro',
          stage: 'Term Sheet',
          sector: 'Healthcare',
          leadPartner: 'J. Smith',
          checkSize: 12000000,
          daysInStage: 8,
        },
        {
          id: '2',
          company: 'FinanceAI',
          stage: 'IC Review',
          sector: 'Fintech',
          leadPartner: 'M. Chen',
          checkSize: 10000000,
          daysInStage: 5,
        },
        {
          id: '3',
          company: 'CloudSync',
          stage: 'Due Diligence',
          sector: 'Infrastructure',
          leadPartner: 'J. Smith',
          checkSize: 15000000,
          daysInStage: 14,
        },
        {
          id: '4',
          company: 'Acme Corp',
          stage: 'Partner Meeting',
          sector: 'Enterprise',
          leadPartner: 'A. Johnson',
          checkSize: 8000000,
          daysInStage: 7,
        },
        {
          id: '5',
          company: 'DataFlow AI',
          stage: 'First Look',
          sector: 'AI/ML',
          leadPartner: 'M. Chen',
          checkSize: 3000000,
          daysInStage: 3,
        },
      ],
    },
  },

  // Stale Deals - deals needing attention
  'Stale Deals List': {
    items: [
      { id: '1', title: 'TechVenture', subtitle: 'Partner Meeting - 45 days', status: 'overdue' },
      { id: '2', title: 'GreenEnergy Co', subtitle: 'First Look - 38 days', status: 'overdue' },
      { id: '3', title: 'RetailTech', subtitle: 'Due Diligence - 32 days', status: 'urgent' },
    ],
  },
};

// =============================================================================
// FUNDRAISING MODULE - LP Pipeline Dashboard (Industry Standard)
// =============================================================================

export const fundraisingMockData: Record<string, WidgetConfig> = {
  // PRIMARY KPIs - Industry Standard Fundraising Metrics
  'Total Committed': {
    value: 320000000,
    label: 'Total Committed',
    secondaryLabel: '$500M target',
    format: 'currency',
    color: 'primary',
  },
  '% to Target': {
    value: 64,
    label: 'of Target Raised',
    secondaryLabel: '$180M remaining',
    format: 'percentage',
    color: 'info',
  },
  'LP Re-up Rate': {
    value: 78,
    label: 'LP Re-up Rate',
    secondaryLabel: '18 of 23 Fund III LPs',
    format: 'percentage',
    color: 'success',
  },
  'Avg Commitment': {
    value: 17800000,
    label: 'Avg Commitment Size',
    secondaryLabel: '18 LPs committed',
    format: 'currency',
  },

  // SECONDARY KPIs
  'New LP Conversion': {
    value: 24,
    label: 'New LP Conversion',
    secondaryLabel: '6 of 25 prospects',
    format: 'percentage',
  },
  'Pipeline Value': {
    value: 285000000,
    label: 'Pipeline Value',
    secondaryLabel: '42 prospects',
    format: 'currency',
    color: 'secondary',
  },
  'Days Since Launch': {
    value: 87,
    label: 'Days Since Launch',
    secondaryLabel: 'Launched Sep 8, 2024',
  },
  'First Close': {
    value: '$125M',
    label: 'First Close',
    secondaryLabel: 'Oct 15, 2024',
    color: 'success',
  },

  // Pipeline Funnel - COUNT(FundraisingOpportunity) GROUP BY stage
  'Pipeline Funnel': {
    stages: [
      { label: 'Prospect', value: 42, color: '#94a3b8' },
      { label: 'Contacted', value: 28, color: '#64748b' },
      { label: 'Interested', value: 18, color: '#3b82f6' },
      { label: 'Due Diligence', value: 8, color: '#8b5cf6' },
      { label: 'Soft Commit', value: 4, color: '#f59e0b' },
      { label: 'Hard Commit', value: 2, color: '#10b981' },
    ],
  },

  // LP Composition - Pie chart by LP type
  'LP Composition': {
    data: [
      { label: 'Pension Funds', value: 125 },
      { label: 'Endowments', value: 85 },
      { label: 'Family Offices', value: 55 },
      { label: 'Fund of Funds', value: 35 },
      { label: 'Sovereign Wealth', value: 20 },
    ],
  },

  // Commitment Timeline - Cumulative commitments over time
  'Commitment Timeline': {
    data: [
      { label: 'Sep', value: 0 },
      { label: 'Oct', value: 125 },
      { label: 'Nov', value: 210 },
      { label: 'Dec', value: 320 },
    ],
  },

  // Recent Activity
  'Recent Activity': {
    activities: [
      {
        id: '1',
        type: 'meeting',
        description: 'LP meeting with Smith Family Office',
        timestamp: '3 hours ago',
        entity: 'Fund IV',
      },
      {
        id: '2',
        type: 'document',
        description: 'Sent DDQ to Harvard Endowment',
        timestamp: 'Yesterday',
        entity: 'Fund IV',
      },
      {
        id: '3',
        type: 'deal',
        description: 'Received soft commit from Jones Capital - $15M',
        timestamp: 'Yesterday',
        entity: 'Fund IV',
      },
      {
        id: '4',
        type: 'message',
        description: 'Follow-up call with CalPERS',
        timestamp: '2 days ago',
        entity: 'Fund IV',
      },
    ],
  },

  // Upcoming Meetings
  'Upcoming Meetings': {
    items: [
      { id: '1', title: 'LP Meeting: Stanford Endowment', date: 'Tomorrow, 10:00 AM' },
      { id: '2', title: 'LP Meeting: Tiger Global', date: 'Dec 5, 2:00 PM' },
      { id: '3', title: 'LP Dinner: SuperReturn Conference', date: 'Dec 8, 7:00 PM' },
      { id: '4', title: 'LP Call: Abu Dhabi Investment Authority', date: 'Dec 10, 8:00 AM' },
    ],
  },

  // Top Prospects Table - with days in stage and next action
  'Top Prospects': {
    tableData: {
      columns: [
        { key: 'name', label: 'LP Name', align: 'left' },
        { key: 'type', label: 'Type', align: 'left' },
        { key: 'targetAmount', label: 'Target', format: 'currency', align: 'right' },
        { key: 'stage', label: 'Stage', align: 'center' },
        { key: 'daysInStage', label: 'Days', align: 'right' },
        { key: 'nextAction', label: 'Next Action', align: 'left' },
      ],
      rows: [
        {
          id: '1',
          name: 'CalPERS',
          type: 'Pension',
          targetAmount: 75000000,
          stage: 'Soft Commit',
          daysInStage: 5,
          nextAction: 'Final docs',
        },
        {
          id: '2',
          name: 'Harvard Endowment',
          type: 'Endowment',
          targetAmount: 50000000,
          stage: 'Due Diligence',
          daysInStage: 12,
          nextAction: 'Site visit',
        },
        {
          id: '3',
          name: 'Stanford Endowment',
          type: 'Endowment',
          targetAmount: 40000000,
          stage: 'Interested',
          daysInStage: 8,
          nextAction: 'Send DDQ',
        },
        {
          id: '4',
          name: 'Tiger Global',
          type: 'Fund of Funds',
          targetAmount: 30000000,
          stage: 'Contacted',
          daysInStage: 3,
          nextAction: 'Schedule call',
        },
        {
          id: '5',
          name: 'Smith Family Office',
          type: 'Family Office',
          targetAmount: 25000000,
          stage: 'Due Diligence',
          daysInStage: 18,
          nextAction: 'Reference calls',
        },
      ],
    },
  },
};

// =============================================================================
// IR MODULE - LP Engagement Dashboard (Front Office)
// =============================================================================

export const irMockData: Record<string, WidgetConfig> = {
  // PRIMARY KPIs - LP Engagement Metrics
  'Total LPs': {
    value: 23,
    label: 'Active LPs',
    secondaryLabel: 'Across 2 funds',
    color: 'primary',
  },
  'Avg Days Since Contact': {
    value: 34,
    label: 'Avg Days Since Contact',
    secondaryLabel: '5 LPs > 60 days',
    color: 'warning',
  },
  'Engagement Score': {
    value: 78,
    label: 'Engagement Score',
    secondaryLabel: 'Based on touchpoints',
    format: 'percentage',
    color: 'success',
    trend: 3,
  },
  'Upcoming Meetings': {
    value: 5,
    label: 'LP Meetings',
    secondaryLabel: 'Next 14 days',
    color: 'info',
  },

  // LP Roster by Type - Pie chart
  'LP Roster by Type': {
    data: [
      { label: 'Pension Funds', value: 6 },
      { label: 'Endowments', value: 5 },
      { label: 'Family Offices', value: 7 },
      { label: 'Fund of Funds', value: 3 },
      { label: 'Sovereign Wealth', value: 2 },
    ],
  },

  // LPs Needing Outreach - sorted by days since contact
  'LPs Needing Outreach': {
    tableData: {
      columns: [
        { key: 'name', label: 'LP Name', align: 'left' },
        { key: 'type', label: 'Type', align: 'left' },
        { key: 'daysSinceContact', label: 'Days Silent', align: 'right' },
        { key: 'lastContact', label: 'Last Contact', align: 'left' },
        { key: 'commitment', label: 'Commitment', format: 'currency', align: 'right' },
      ],
      rows: [
        {
          id: '1',
          name: 'Smith Family Trust',
          type: 'Family Office',
          daysSinceContact: 95,
          lastContact: 'Aug 30 - Email',
          commitment: 15000000,
        },
        {
          id: '2',
          name: 'Midwest Pension Fund',
          type: 'Pension',
          daysSinceContact: 78,
          lastContact: 'Sep 16 - Call',
          commitment: 45000000,
        },
        {
          id: '3',
          name: 'Alpine Capital Partners',
          type: 'Fund of Funds',
          daysSinceContact: 67,
          lastContact: 'Sep 27 - Meeting',
          commitment: 20000000,
        },
        {
          id: '4',
          name: 'Texas Teachers Retirement',
          type: 'Pension',
          daysSinceContact: 62,
          lastContact: 'Oct 2 - Email',
          commitment: 35000000,
        },
        {
          id: '5',
          name: 'Chen Family Office',
          type: 'Family Office',
          daysSinceContact: 58,
          lastContact: 'Oct 6 - Call',
          commitment: 10000000,
        },
      ],
    },
  },

  // Recent Communications
  'Recent Communications': {
    activities: [
      {
        id: '1',
        type: 'message',
        description: 'Email from Harvard Endowment re: Q3 report',
        timestamp: '1 hour ago',
        entity: 'Harvard Endowment',
      },
      {
        id: '2',
        type: 'meeting',
        description: 'Quarterly call with CalPERS',
        timestamp: '3 hours ago',
        entity: 'CalPERS',
      },
      {
        id: '3',
        type: 'document',
        description: 'Sent investor update to all LPs',
        timestamp: 'Yesterday',
        entity: '23 LPs',
      },
      {
        id: '4',
        type: 'meeting',
        description: 'Annual meeting with Tiger Global',
        timestamp: 'Yesterday',
        entity: 'Tiger Global LP',
      },
      {
        id: '5',
        type: 'message',
        description: 'Follow-up email to Stanford Endowment',
        timestamp: '2 days ago',
        entity: 'Stanford Endowment',
      },
    ],
  },

  // Upcoming LP Meetings
  'Upcoming LP Meetings': {
    items: [
      { id: '1', title: 'Quarterly Review: CalPERS', date: 'Tomorrow, 10:00 AM' },
      { id: '2', title: 'Annual Meeting: Harvard Endowment', date: 'Dec 5, 2:00 PM' },
      { id: '3', title: 'Portfolio Review: Tiger Global', date: 'Dec 8, 11:00 AM' },
      { id: '4', title: 'Intro Call: New LP Prospect', date: 'Dec 10, 3:00 PM' },
    ],
  },

  // LP Engagement Health - who needs attention
  'LP Engagement Health': {
    items: [
      {
        id: '1',
        title: 'Smith Family Trust',
        subtitle: 'No contact in 95 days',
        status: 'overdue',
      },
      {
        id: '2',
        title: 'Midwest Pension Fund',
        subtitle: 'No contact in 78 days',
        status: 'overdue',
      },
      {
        id: '3',
        title: 'Alpine Capital Partners',
        subtitle: 'No contact in 67 days',
        status: 'urgent',
      },
      {
        id: '4',
        title: 'Texas Teachers Retirement',
        subtitle: 'Missed last quarterly call',
        status: 'urgent',
      },
    ],
  },
};

// =============================================================================
// IR MODULE - Capital Activity Dashboard (Back Office)
// =============================================================================

export const irCapitalMockData: Record<string, WidgetConfig> = {
  // PRIMARY KPIs - Capital Activity Metrics
  'Outstanding Capital Calls': {
    value: 8500000,
    label: 'Outstanding Capital Calls',
    secondaryLabel: '12 LPs pending',
    format: 'currency',
    color: 'warning',
  },
  'Collection Rate': {
    value: 94,
    label: 'Collection Rate',
    secondaryLabel: 'Last 12 months',
    format: 'percentage',
    color: 'success',
  },
  'Pending Distributions': {
    value: 12400000,
    label: 'Pending Distributions',
    secondaryLabel: 'Q4 distribution',
    format: 'currency',
    color: 'info',
  },
  'Reports Pending': {
    value: 3,
    label: 'Reports Pending',
    secondaryLabel: 'K-1s due Jan 31',
    color: 'primary',
  },

  // Capital Call Status - Pie chart
  'Capital Call Status': {
    data: [
      { label: 'Paid', value: 45 },
      { label: 'Pending', value: 8 },
      { label: 'Overdue', value: 3 },
    ],
  },

  // Overdue Capital Calls
  'Overdue Capital Calls': {
    items: [
      {
        id: '1',
        title: 'Smith Family Trust',
        subtitle: 'Call #5 - $500K',
        status: 'overdue',
        date: '5 days overdue',
      },
      {
        id: '2',
        title: 'Jones Capital LLC',
        subtitle: 'Call #5 - $750K',
        status: 'overdue',
        date: '2 days overdue',
      },
      {
        id: '3',
        title: 'Alpine Capital Partners',
        subtitle: 'Call #5 - $320K',
        status: 'overdue',
        date: '1 day overdue',
      },
    ],
  },

  // Pending Capital Calls
  'Pending Capital Calls': {
    tableData: {
      columns: [
        { key: 'lp', label: 'LP', align: 'left' },
        { key: 'callNumber', label: 'Call #', align: 'center' },
        { key: 'amount', label: 'Amount', format: 'currency', align: 'right' },
        { key: 'dueDate', label: 'Due Date', align: 'left' },
        { key: 'status', label: 'Status', align: 'center' },
      ],
      rows: [
        {
          id: '1',
          lp: 'Tiger Global LP',
          callNumber: 5,
          amount: 1200000,
          dueDate: 'Dec 4',
          status: 'Due Tomorrow',
        },
        {
          id: '2',
          lp: 'Harvard Endowment',
          callNumber: 5,
          amount: 2500000,
          dueDate: 'Dec 6',
          status: 'Pending',
        },
        {
          id: '3',
          lp: 'CalPERS',
          callNumber: 5,
          amount: 3750000,
          dueDate: 'Dec 8',
          status: 'Pending',
        },
        {
          id: '4',
          lp: 'Stanford Endowment',
          callNumber: 5,
          amount: 2000000,
          dueDate: 'Dec 10',
          status: 'Pending',
        },
      ],
    },
  },

  // Upcoming Distributions
  'Upcoming Distributions': {
    items: [
      { id: '1', title: 'Q4 2024 Distribution', subtitle: '$12.4M to 23 LPs', date: 'Dec 15' },
      {
        id: '2',
        title: 'HealthTech Pro Exit Proceeds',
        subtitle: '$8.2M to 23 LPs',
        date: 'Dec 20',
      },
    ],
  },

  // Report Distribution Status
  'Report Distribution Status': {
    tableData: {
      columns: [
        { key: 'report', label: 'Report', align: 'left' },
        { key: 'dueDate', label: 'Due', align: 'left' },
        { key: 'sent', label: 'Sent', align: 'right' },
        { key: 'acknowledged', label: 'Acknowledged', align: 'right' },
        { key: 'status', label: 'Status', align: 'center' },
      ],
      rows: [
        {
          id: '1',
          report: 'Q3 2024 Quarterly Report',
          dueDate: 'Nov 15',
          sent: 23,
          acknowledged: 21,
          status: 'Complete',
        },
        {
          id: '2',
          report: '2023 K-1s',
          dueDate: 'Mar 15',
          sent: 23,
          acknowledged: 23,
          status: 'Complete',
        },
        {
          id: '3',
          report: '2024 K-1s',
          dueDate: 'Jan 31',
          sent: 0,
          acknowledged: 0,
          status: 'Pending',
        },
      ],
    },
  },

  // Quarterly Calendar
  'Quarterly Calendar': {
    items: [
      { id: '1', title: 'Q4 Capital Call Due', date: 'Dec 10' },
      { id: '2', title: 'Q4 Distribution', date: 'Dec 15' },
      { id: '3', title: 'Q4 Quarterly Report Due', date: 'Jan 15' },
      { id: '4', title: '2024 K-1s Due', date: 'Jan 31' },
    ],
  },
};

// =============================================================================
// PORTFOLIO MODULE - Portfolio Health Dashboard (Industry Standard)
// =============================================================================

export const portfolioMockData: Record<string, WidgetConfig> = {
  // PRIMARY KPIs - Industry Standard Fund Metrics
  // TVPI = (Distributions + NAV) / Paid-In Capital
  TVPI: {
    value: 1.82,
    label: 'Total Value to Paid-In',
    secondaryLabel: 'Realized + Unrealized',
    format: 'multiple',
    color: 'primary',
  },
  // DPI = Distributions / Paid-In Capital
  DPI: {
    value: 0.45,
    label: 'Distributions to Paid-In',
    secondaryLabel: '$64M returned of $142M',
    format: 'multiple',
    color: 'info',
  },
  // RVPI = NAV / Paid-In Capital
  RVPI: {
    value: 1.37,
    label: 'Residual Value to Paid-In',
    secondaryLabel: '$194M unrealized',
    format: 'multiple',
    color: 'secondary',
  },
  // Net IRR
  'Net IRR': {
    value: 18.2,
    label: 'Net IRR',
    secondaryLabel: 'Since inception (2019)',
    format: 'percentage',
    trend: 2,
    color: 'success',
  },

  // SECONDARY KPIs
  'Total Invested': {
    value: 142000000,
    label: 'Total Invested',
    format: 'currency',
    color: 'primary',
  },
  'Current NAV': {
    value: 258000000,
    label: 'Current NAV',
    format: 'currency',
    trend: 8,
  },
  'Realized Gains': {
    value: 64000000,
    label: 'Realized Gains',
    format: 'currency',
  },
  'Active Companies': {
    value: 24,
    label: 'Active Companies',
    secondaryLabel: '3 exited, 1 written off',
  },

  // Portfolio Performance Table - Industry standard view with context
  'Portfolio Performance': {
    tableData: {
      columns: [
        { key: 'company', label: 'Company', align: 'left' },
        { key: 'invested', label: 'Invested', format: 'currency', align: 'right' },
        { key: 'currentValue', label: 'Current Value', format: 'currency', align: 'right' },
        { key: 'moic', label: 'MOIC', format: 'multiple', align: 'right' },
        { key: 'irr', label: 'IRR', format: 'percentage', align: 'right' },
        { key: 'pctOfFund', label: '% of Fund', format: 'percentage', align: 'right' },
        { key: 'status', label: 'Status', align: 'center' },
      ],
      rows: [
        {
          id: '1',
          company: 'HealthTech Pro',
          invested: 4000000,
          currentValue: 16800000,
          moic: 4.2,
          irr: 42.1,
          pctOfFund: 6.5,
          status: 'Unrealized',
        },
        {
          id: '2',
          company: 'DevTools Inc',
          invested: 2500000,
          currentValue: 9500000,
          moic: 3.8,
          irr: 35.2,
          pctOfFund: 3.7,
          status: 'Unrealized',
        },
        {
          id: '3',
          company: 'FinanceAI',
          invested: 6000000,
          currentValue: 17400000,
          moic: 2.9,
          irr: 28.5,
          pctOfFund: 6.7,
          status: 'Unrealized',
        },
        {
          id: '4',
          company: 'SaaS Co',
          invested: 5500000,
          currentValue: 13200000,
          moic: 2.4,
          irr: 22.3,
          pctOfFund: 5.1,
          status: 'Unrealized',
        },
        {
          id: '5',
          company: 'DataFlow AI',
          invested: 3000000,
          currentValue: 6300000,
          moic: 2.1,
          irr: 18.7,
          pctOfFund: 2.4,
          status: 'Unrealized',
        },
        {
          id: '6',
          company: 'CloudSync',
          invested: 8000000,
          currentValue: 14400000,
          moic: 1.8,
          irr: 15.2,
          pctOfFund: 5.6,
          status: 'Unrealized',
        },
        {
          id: '7',
          company: 'Acme Corp',
          invested: 5000000,
          currentValue: 7500000,
          moic: 1.5,
          irr: 10.8,
          pctOfFund: 2.9,
          status: 'Unrealized',
        },
        {
          id: '8',
          company: 'AI Platform Co',
          invested: 7000000,
          currentValue: 8400000,
          moic: 1.2,
          irr: 5.2,
          pctOfFund: 3.3,
          status: 'Unrealized',
        },
      ],
    },
  },

  // Runway Risk Matrix - with proper axis labels
  // X: Runway in months, Y: Monthly burn rate (in $K for readability)
  'Runway Risk Matrix': {
    scatterData: [
      { x: 3, y: 450, id: 'acme', label: 'Acme Corp', size: 5000000 },
      { x: 5, y: 320, id: 'cloudsync', label: 'CloudSync', size: 8000000 },
      { x: 8, y: 180, id: 'dataflow', label: 'DataFlow AI', size: 3000000 },
      { x: 14, y: 250, id: 'financeai', label: 'FinanceAI', size: 6000000 },
      { x: 18, y: 120, id: 'healthtech', label: 'HealthTech Pro', size: 4000000 },
      { x: 24, y: 80, id: 'devtools', label: 'DevTools Inc', size: 2500000 },
      { x: 4, y: 380, id: 'aiplatform', label: 'AI Platform Co', size: 7000000 },
      { x: 12, y: 200, id: 'saasco', label: 'SaaS Co', size: 5500000 },
    ],
    xAxisLabel: 'Runway (months)',
    yAxisLabel: 'Monthly Burn ($K)',
    showQuadrants: true,
  },

  // Recent Submissions - Document nodes processed by Diggens
  'Recent Submissions': {
    activities: [
      {
        id: '1',
        type: 'document',
        description: 'Q3 2024 Board Deck processed',
        timestamp: '2 hours ago',
        entity: 'Acme Corp',
      },
      {
        id: '2',
        type: 'document',
        description: 'Monthly financials extracted',
        timestamp: 'Yesterday',
        entity: 'CloudSync',
      },
      {
        id: '3',
        type: 'document',
        description: 'Q3 2024 Board Deck processed',
        timestamp: 'Yesterday',
        entity: 'FinanceAI',
      },
      {
        id: '4',
        type: 'document',
        description: 'Investor update processed',
        timestamp: '2 days ago',
        entity: 'HealthTech Pro',
      },
      {
        id: '5',
        type: 'document',
        description: 'Q3 2024 Board Deck processed',
        timestamp: '3 days ago',
        entity: 'DataFlow AI',
      },
    ],
  },

  // Alerts - derived from Organization AMFI Metrics
  Alerts: {
    items: [
      {
        id: '1',
        title: 'Acme Corp',
        subtitle: 'Runway: 3.2 months — needs bridge',
        status: 'overdue',
      },
      {
        id: '2',
        title: 'AI Platform Co',
        subtitle: 'Runway: 4.1 months — monitor closely',
        status: 'overdue',
      },
      {
        id: '3',
        title: 'CloudSync',
        subtitle: 'Runway: 5.0 months — fundraising',
        status: 'urgent',
      },
      { id: '4', title: 'DevTools Inc', subtitle: 'Missing Q3 financials', status: 'urgent' },
      {
        id: '5',
        title: 'SaaS Co',
        subtitle: 'Board meeting in 3 days — no deck',
        status: 'urgent',
      },
    ],
  },

  // Upcoming Board Meetings
  'Upcoming Board Meetings': {
    items: [
      { id: '1', title: 'Acme Corp Board Meeting', date: 'Dec 5, 10:00 AM' },
      { id: '2', title: 'CloudSync Board Meeting', date: 'Dec 8, 2:00 PM' },
      { id: '3', title: 'FinanceAI Board Meeting', date: 'Dec 12, 11:00 AM' },
      { id: '4', title: 'HealthTech Pro Board Meeting', date: 'Dec 15, 9:00 AM' },
    ],
  },
};

// =============================================================================
// HELPER: Get mock data for a widget by module and widget name
// =============================================================================

export function getMockDataForWidget(module: string, widgetName: string): WidgetConfig {
  const moduleData: Record<string, Record<string, WidgetConfig>> = {
    'pevc-dealflow': dealFlowMockData,
    'pevc-fundraising': fundraisingMockData,
    'pevc-ir': irMockData,
    'pevc-portfolio': portfolioMockData,
  };

  // First try the module's data
  let data = moduleData[module]?.[widgetName];

  // For IR module, also check capital data (for non-default dashboards)
  if (!data && module === 'pevc-ir') {
    data = irCapitalMockData[widgetName];
  }

  if (!data) {
    // Return empty placeholder
    return {};
  }

  return data;
}

export type { WidgetConfig };
